import jsPDF from 'jspdf';
import { Invoice, InvoiceTemplate } from '../types';

export const generateInvoicePDF = (invoice: Invoice, template?: InvoiceTemplate): jsPDF => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Colors from template or default
  const colors = template?.colors || {
    primary: '#2563EB',
    secondary: '#1E40AF',
    accent: '#3B82F6',
    text: '#374151',
    background: '#FFFFFF'
  };
  
  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 37, g: 99, b: 235 };
  };
  
  const primaryRgb = hexToRgb(colors.primary);
  const backgroundRgb = hexToRgb(colors.background);
  const textRgb = hexToRgb(colors.text);
  
  // Header
  pdf.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
  pdf.rect(0, 0, pageWidth, 40, 'F');
  
  // Add logo if template has one
  if (template?.logo) {
    // In a real implementation, you would load and add the logo image
    // For now, we'll add a placeholder circle
    pdf.setFillColor(255, 255, 255);
    pdf.circle(25, 20, 8, 'F');
    pdf.setTextColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('LOGO', 20, 23);
  }
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('INVOICE', template?.logo ? 50 : 20, 25);
  
  // Invoice details section
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Invoice #${invoice.invoiceNumber}`, pageWidth - 80, 15);
  pdf.text(`Date: ${invoice.date}`, pageWidth - 80, 25);
  pdf.text(`Due: ${invoice.dueDate}`, pageWidth - 80, 35);
  
  if (invoice.terms) {
    pdf.text(`Terms: ${invoice.terms}`, pageWidth - 80, 45);
  }
  
  if (invoice.orderNumber) {
    pdf.text(`Order #: ${invoice.orderNumber}`, pageWidth - 80, invoice.terms ? 55 : 45);
  }
  
  // Reset text color
  pdf.setTextColor(textRgb.r, textRgb.g, textRgb.b);
  
  // Company info (From)
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('From:', 20, 60);
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  let yPos = 70;
  pdf.text(invoice.company.name, 20, yPos);
  yPos += 6;
  
  if (invoice.company.businessName) {
    pdf.text(invoice.company.businessName, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.company.email) {
    pdf.text(invoice.company.email, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.company.address) {
    const addressLines = invoice.company.address.split('\n');
    addressLines.forEach(line => {
      pdf.text(line, 20, yPos);
      yPos += 6;
    });
  }
  
  if (invoice.company.phone) {
    pdf.text(`Phone: ${invoice.company.phone}`, 20, yPos);
    yPos += 6;
  }

  if (invoice.company.mobile) {
    pdf.text(`Mobile: ${invoice.company.mobile}`, 20, yPos);
    yPos += 6;
  }

  if (invoice.company.fax) {
    pdf.text(`Fax: ${invoice.company.fax}`, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.company.businessNumber) {
    pdf.text(`Business #: ${invoice.company.businessNumber}`, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.company.website) {
    pdf.text(invoice.company.website, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.company.additionalDetails) {
    // If additionalDetails is true, we display any additional information available
    // This would typically be custom fields or other company information
    yPos += 2;
    pdf.setFont('helvetica', 'italic');
    pdf.text('Additional Business Information:', 20, yPos);
    yPos += 6;
    pdf.setFont('helvetica', 'normal');
  }
  
  // Adicionar espaço entre as seções
  yPos += 10;
  
  // Bill to info - Posicionando abaixo das informações da empresa
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Bill To:', 20, yPos);
  yPos += 10;
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(invoice.billTo.name, 20, yPos);
  yPos += 6;
  
  if (invoice.billTo.email) {
    pdf.text(`Email: ${invoice.billTo.email}`, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.billTo.address) {
    const addressLines = invoice.billTo.address.split('\n');
    addressLines.forEach(line => {
      pdf.text(line, 20, yPos);
      yPos += 6;
    });
  }
  
  if (invoice.billTo.phone) {
    pdf.text(`Phone: ${invoice.billTo.phone}`, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.billTo.mobile) {
    pdf.text(`Mobile: ${invoice.billTo.mobile}`, 20, yPos);
    yPos += 6;
  }
  
  if (invoice.billTo.fax) {
    pdf.text(`Fax: ${invoice.billTo.fax}`, 20, yPos);
    yPos += 6;
  }
  
  // Items table - Ajustando a posição inicial da tabela para ficar abaixo das informações do cliente
  const tableStartY = yPos + 20;
  
  // Table header
  const headerRgb = hexToRgb(colors.background === '#FFFFFF' ? '#F3F4F6' : colors.background);
  pdf.setFillColor(headerRgb.r, headerRgb.g, headerRgb.b);
  pdf.rect(20, tableStartY - 8, pageWidth - 40, 15, 'F');
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Description', 25, tableStartY);
  pdf.text('Qty', 120, tableStartY);
  pdf.text('Rate', 140, tableStartY);
  pdf.text('Amount', pageWidth - 30, tableStartY, { align: 'right' });
  
  // Table rows
  let currentY = tableStartY + 15;
  pdf.setFont('helvetica', 'normal');
  
  invoice.items.forEach((item, index) => {
    if (currentY > pageHeight - 40) {
      pdf.addPage();
      currentY = 40;
    }
    
    // Handle multiline descriptions
    const maxDescWidth = 85; // Width available for description text
    const descLines = pdf.splitTextToSize(item.description, maxDescWidth);
    
    // Calculate text height
    const descHeight = descLines.length * 5;
    
    // Draw description lines
    for (let i = 0; i < descLines.length; i++) {
      pdf.text(descLines[i], 25, currentY + (i * 5));
    }
    
    // Update currentY to account for multiline description
    const initialY = currentY;
    currentY += Math.max((descLines.length - 1) * 5, 0);
    
    // Add additional details if available
    if (item.additionalDetails) {
      pdf.setFontSize(9);
      const detailLines = pdf.splitTextToSize(item.additionalDetails, maxDescWidth);
      
      // Add spacing after description
      currentY += 5; // Aumentado de 3 para 5
      
      // Draw additional details
      for (let i = 0; i < detailLines.length; i++) {
        pdf.text(detailLines[i], 25, currentY + 5 + (i * 5)); // Aumentado de 4 para 5
      }
      
      // Update currentY for additional details
      currentY += detailLines.length * 5 + 2; // Aumentado de 4 para 5 e adicionado 2 de espaço extra
      pdf.setFontSize(11);
    }
    
    // Draw other item columns at the first line height
    pdf.text(item.quantity.toString(), 120, initialY);
    pdf.text(`${invoice.currency} ${item.rate.toFixed(2)}`, 140, initialY);
    pdf.text(`${invoice.currency} ${item.amount.toFixed(2)}`, pageWidth - 30, initialY, { align: 'right' });
    
    currentY += 15; // Aumentado de 12 para 15 para mais espaço entre itens
    
    // Add line separator
    if (index < invoice.items.length - 1) {
      pdf.setDrawColor(229, 231, 235);
      pdf.line(20, currentY - 6, pageWidth - 20, currentY - 6);
    }
  });
  
  // Totals
  currentY += 10;
  const totalsX = pageWidth - 80;
  
  pdf.setFont('helvetica', 'normal');
  pdf.text('Subtotal:', totalsX - 40, currentY);
  pdf.text(`${invoice.currency} ${invoice.subtotal.toFixed(2)}`, pageWidth - 30, currentY, { align: 'right' });
  currentY += 10;
  
  if (invoice.tax > 0) {
    pdf.text(`Tax (${invoice.taxRate}%):`, totalsX - 40, currentY);
    pdf.text(`${invoice.currency} ${invoice.tax.toFixed(2)}`, pageWidth - 30, currentY, { align: 'right' });
    currentY += 10;
  }
  
  // Total line
  pdf.setDrawColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
  pdf.line(totalsX - 40, currentY - 5, pageWidth - 20, currentY - 5);
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Total:', totalsX - 40, currentY + 5);
  pdf.setTextColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
  pdf.text(`${invoice.currency} ${invoice.total.toFixed(2)}`, pageWidth - 30, currentY + 5, { align: 'right' });
  
  // Amount Paid and Balance Due
  if (invoice.amountPaid !== undefined) {
    currentY += 15;
    pdf.setTextColor(textRgb.r, textRgb.g, textRgb.b);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text('Amount Paid:', totalsX - 40, currentY);
    pdf.text(`${invoice.currency} ${invoice.amountPaid.toFixed(2)}`, pageWidth - 30, currentY, { align: 'right' });
    
    // Calculate balance due if not provided
    const balanceDue = invoice.balanceDue !== undefined ? 
      invoice.balanceDue : 
      invoice.total - invoice.amountPaid;
    
    currentY += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Balance Due:', totalsX - 40, currentY);
    pdf.setTextColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    pdf.text(`${invoice.currency} ${balanceDue.toFixed(2)}`, pageWidth - 30, currentY, { align: 'right' });
  }
  
  pdf.setTextColor(textRgb.r, textRgb.g, textRgb.b);
  
  // Notes Section
  if (invoice.notes) {
    currentY += 30;
    
    // Ensure we have enough space for the notes or add a new page
    if (currentY + 50 > pageHeight) {
      pdf.addPage();
      currentY = 40;
    }
    
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(textRgb.r, textRgb.g, textRgb.b);
    pdf.setFontSize(12);
    pdf.text('Notes:', 20, currentY);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    
    const notesLines = invoice.notes.split('\n');
    
    notesLines.forEach((line, index) => {
      const textHeight = pdf.getTextDimensions(line, { maxWidth: pageWidth - 40 }).h;
      
      if (currentY + textHeight + 5 > pageHeight) {
        pdf.addPage();
        currentY = 40;
      }
      
      pdf.text(line, 20, currentY + 10 + (index * 5));
      currentY += textHeight + 2;
    });
  }
  
  // Signature Section
  if (invoice.signature) {
    currentY += 30;
    
    // Ensure we have enough space for the signature or add a new page
    if (currentY + 60 > pageHeight) {
      pdf.addPage();
      currentY = 40;
    }
    
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(textRgb.r, textRgb.g, textRgb.b);
    pdf.setFontSize(12);
    pdf.text('Signature:', 20, currentY);
    currentY += 10;
    
    // Add signature image
    try {
      if (invoice.signature.startsWith('data:image')) {
        pdf.addImage(invoice.signature, 'PNG', 20, currentY, 50, 30, undefined, 'FAST');
        currentY += 35;
      }
    } catch (error) {
      console.error('Error adding signature to PDF:', error);
    }
  }
  
  // Photos Section
  if (invoice.photos && invoice.photos.length > 0) {
    currentY += 20;
    
    // Ensure we have enough space for the photos heading or add a new page
    if (currentY + 15 > pageHeight) {
      pdf.addPage();
      currentY = 40;
    }
    
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(textRgb.r, textRgb.g, textRgb.b);
    pdf.setFontSize(12);
    pdf.text('Photos:', 20, currentY);
    currentY += 15;
    
    // Add photos in a grid layout
    const photoWidth = 70;
    const photoHeight = 70;
    const photosPerRow = 2;
    const horizontalSpacing = 20;
    
    for (let i = 0; i < invoice.photos.length; i++) {
      const photo = invoice.photos[i];
      const row = Math.floor(i / photosPerRow);
      const col = i % photosPerRow;
      
      const photoX = 20 + (col * (photoWidth + horizontalSpacing));
      let photoY = currentY + (row * (photoHeight + 30)); // Increased spacing between rows
      
      // Add a new page if needed
      if (photoY + photoHeight > pageHeight) {
        pdf.addPage();
        currentY = 40;
        // Reset row count for the new page
        photoY = currentY + ((i % photosPerRow === 0 ? 0 : 1) * (photoHeight + 30));
      }
      
      // Add photo image
      try {
        if (photo.startsWith('data:image')) {
          pdf.addImage(photo, 'PNG', photoX, photoY, photoWidth, photoHeight, undefined, 'FAST');
        }
      } catch (error) {
        console.error(`Error adding photo ${i} to PDF:`, error);
      }
    }
    
    // Update currentY to after all photos
    const rows = Math.ceil(invoice.photos.length / photosPerRow);
    currentY += (rows * (photoHeight + 30)); // Increased spacing between rows
  }
  
  return pdf;
};